import { useMemo } from "react";
import { cn } from "../ui/cn";

// ---------------------------------------------------------------------------
// Línea de tiempo de los periodos de nómina (CC-CONSOLE#1595): cada periodo
// es un tramo de su inicio a su fin sobre un eje de fechas, con el día de
// pago marcado, su estado en color y, debajo, cuántos recibos tiene y su
// neto. Una línea vertical marca «hoy».
// ---------------------------------------------------------------------------

export interface TimelinePeriod {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  payDate?: string | null;
  status?: string | null;
  /** Recibos del periodo y su neto (ya formateado), si se conocen. */
  payslips?: number;
  net?: string | null;
}

export const PERIOD_TONES: Record<string, { bar: string; chip: string }> = {
  closed: { bar: "bg-success/70", chip: "bg-success/15 text-success" },
  calculated: { bar: "bg-warning/70", chip: "bg-warning/15 text-warning" },
  open: { bar: "bg-primary-500/70", chip: "bg-primary-500/15 text-primary-500" },
};

const toMs = (d: string) => Date.parse(`${d}T12:00:00Z`);

interface Props {
  periods: TimelinePeriod[];
  today: string;
  title: string;
  todayLabel: string;
  payDayLabel: string;
  statusLabel: (status: string) => string;
  payslipsLabel: (count: number) => string;
  emptyLabel: string;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}

export default function PeriodTimeline({ periods, today, title, todayLabel, payDayLabel, statusLabel, payslipsLabel, emptyLabel, selectedId, onSelect }: Props) {
  const sorted = useMemo(() => [...periods].filter((p) => p.startDate && p.endDate).sort((a, b) => a.startDate.localeCompare(b.startDate)).slice(-8), [periods]);
  const span = useMemo(() => {
    if (!sorted.length) return null;
    const min = toMs(sorted[0].startDate);
    const max = Math.max(...sorted.map((p) => toMs(p.payDate && p.payDate > p.endDate ? p.payDate : p.endDate)), toMs(today));
    return { min: Math.min(min, toMs(today)), max: max + 86400000 };
  }, [sorted, today]);

  if (!sorted.length || !span) {
    return (
      <div className="rounded-xl border border-line bg-card p-6 text-center text-sm text-content-secondary" data-testid="period-timeline-empty">
        {emptyLabel}
      </div>
    );
  }
  const pct = (d: string) => ((toMs(d) - span.min) / (span.max - span.min)) * 100;
  const todayPct = pct(today);

  return (
    <section className="rounded-xl border border-line bg-card p-4" data-testid="period-timeline">
      <h3 className="mb-3 text-sm font-semibold text-content">
        <i className="ri-time-line mr-1.5 text-primary-500" aria-hidden="true" />
        {title}
      </h3>
      <div className="relative">
        {/* Eje con la marca de hoy */}
        <div className="pointer-events-none absolute bottom-0 top-0 z-10 w-px bg-error" style={{ left: `${todayPct}%` }} data-testid="timeline-today">
          <span className="absolute -top-1 left-1 whitespace-nowrap rounded bg-error px-1 text-[10px] font-semibold text-white">{todayLabel}</span>
        </div>
        <ol className="space-y-2 pt-4">
          {sorted.map((p) => {
            const status = p.status || "open";
            const tone = PERIOD_TONES[status] ?? PERIOD_TONES.open;
            const left = pct(p.startDate);
            const width = Math.max(2, pct(p.endDate) + (100 / ((span.max - span.min) / 86400000)) - left);
            const selected = selectedId === p.id;
            return (
              <li key={p.id} data-testid={`timeline-period-${p.id}`} data-status={status}>
                <button
                  type="button"
                  onClick={() => onSelect?.(p.id)}
                  className={cn("group relative block h-12 w-full rounded-lg text-left", selected ? "bg-primary-500/10" : "hover:bg-subtle")}
                  aria-pressed={selected}
                >
                  <span className="absolute inset-y-2 rounded-md shadow-sm" style={{ left: `${left}%`, width: `${width}%` }}>
                    <span className={cn("absolute inset-0 rounded-md", tone.bar)} />
                    <span className="relative flex h-full items-center gap-2 overflow-hidden px-2 text-[11px] font-semibold text-white">
                      <span className="truncate">{p.name}</span>
                    </span>
                  </span>
                  {p.payDate && (
                    <span className="absolute inset-y-1 w-0.5 rounded bg-content" style={{ left: `${pct(p.payDate) + 100 / ((span.max - span.min) / 86400000) - 0.4}%` }} title={`${payDayLabel}: ${p.payDate}`} />
                  )}
                </button>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 px-1 text-[11px] text-content-secondary">
                  <span className={cn("rounded-full px-2 py-0.5 font-medium", tone.chip)}>{statusLabel(status)}</span>
                  <span>
                    {p.startDate} → {p.endDate}
                  </span>
                  {p.payDate && (
                    <span>
                      <i className="ri-bank-card-line mr-0.5" aria-hidden="true" />
                      {payDayLabel}: {p.payDate}
                    </span>
                  )}
                  {p.payslips != null && <span>· {payslipsLabel(p.payslips)}</span>}
                  {p.net && <span className="font-semibold text-content">· {p.net}</span>}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
